/**
 * Survival Codex — cart drawer extras.
 *
 * Mounts #codex-cart-extras into Dawn's cart drawer and keeps it there.
 *
 * Why a MutationObserver rather than Dawn's pub/sub: CartDrawer.renderContents()
 * replaces the innerHTML of the whole cart-drawer section on every cart change,
 * so anything injected inside it is destroyed on the next add or quantity tweak.
 * Subscribing to 'cart-update' would catch most of those, but not a render
 * triggered from code that does not publish. Watching the drawer for our node
 * going missing catches every case, including ones added by future Dawn updates.
 *
 * The extras block lives at body level in the page and is MOVED into the drawer,
 * never cloned, so there is only ever one of it and its listeners survive.
 */
(function () {
  var CFG = window.CODEX_CART_CFG;
  if (!CFG) return;
  var T = CFG.strings;
  var mounting = false;
  var LAST = null;   // the last /cart.js read; tells ADD whether a line for the variant already exists

  /* 2026-10-27: Dawn has no Shopify.formatMoney, so every non-USD market got "$849" / "$1615.50"
     here. CFG.moneySample is 1,234.56 as Shopify formats it for the visitor's market ("1 234,56 kr",
     "€1.234,56", "£1,234.56"); copy its symbol, position and separators. Whole amounts drop the
     decimals, as before. */
  var MONEY = (function () {
    var m = /^([\s\S]*?)1([^0-9]?)234([^0-9])56([\s\S]*)$/.exec(CFG.moneySample || '');
    return m ? { pre: m[1], group: m[2], dec: m[3], post: m[4] } : null;
  })();
  function money(cents) {
    cents = Math.round(cents);
    if (!MONEY) return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    var whole = String(Math.floor(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, MONEY.group);
    var frac = cents % 100;
    return MONEY.pre + whole + (frac ? MONEY.dec + (frac < 10 ? '0' : '') + frac : '') + MONEY.post;
  }
  function el(id) { return document.getElementById(id); }

  /**
   * The extras block is MOVED into the drawer footer, and CartDrawer.renderContents()
   * replaces the innerHTML of #CartDrawer -- which the footer is inside. So every render
   * after the one that mounted it DESTROYS the node. getElementById then returns null,
   * render() returned early on `if (!extras) return`, and watch()'s missing-node guard
   * read it as page teardown and stopped observing. The result: the add-ons showed up
   * the first time the drawer opened and were gone for the rest of the session, which is
   * why this looked intermittent rather than broken.
   *
   * Holding the reference keeps the node alive even while it is detached, so it can be
   * put back at body level and re-mounted. Everything inside it -- the rendered rows and
   * their state -- comes back with it.
   */
  var EXTRAS = null;
  function extrasNode() {
    if (!EXTRAS) EXTRAS = document.getElementById('codex-cart-extras');
    if (EXTRAS && !document.body.contains(EXTRAS)) document.body.appendChild(EXTRAS);
    return EXTRAS;
  }
  // The free-shipping line used to be a second injected node under the checkout button. Since
  // 2026-10-28 it sits at the top and the cancellation line under the button, both rendered by
  // snippets/cart-drawer.liquid itself, so nothing here has to keep them alive.

  /* ---------------------------------------------------------------- mount -- */

  /**
   * Dawn leaves a stale `is-empty` on <cart-drawer> after the cart stops being
   * empty, and the styling that hangs off it makes the drawer look broken.
   *
   * CartDrawer.renderContents() replaces the innerHTML of #CartDrawer, and
   * CartDrawerItems replaces the innerHTML of .drawer__inner. The `is-empty`
   * class Dawn keys its layout off sits on the <cart-drawer> HOST, which is
   * OUTSIDE both, so nothing ever rewrites it. Add the first item to an empty
   * cart and the class survives, taking `cart-drawer.is-empty .drawer__inner
   * { display: grid; grid-template-rows: 1fr; align-items: center; padding: 0 }`
   * and `cart-drawer.is-empty .drawer__header { display: none }` with it: the
   * header disappears and the items and footer are squeezed into one centred
   * grid row with no padding, which reads as an empty black panel. It also
   * broke open()'s focus trap, which looks for .drawer__inner-empty on a class
   * that says empty but a DOM that is not.
   *
   * The server-rendered markup does carry a correct signal on an element that
   * IS inside the replaced region: <cart-drawer-items class=" is-empty">. Read
   * the class off that and mirror it onto the host. Cheap, and it self-corrects
   * in both directions, including removing the last line item.
   */
  function syncEmpty(drawer) {
    var d = drawer || document.querySelector('cart-drawer');
    if (!d) return null;
    var items = d.querySelector('cart-drawer-items');
    var empty = items ? items.classList.contains('is-empty')
                      : !!d.querySelector('.drawer__inner-empty');
    d.classList.toggle('is-empty', empty);
    return empty;
  }
  window.codexSyncCartDrawer = syncEmpty;

  function drawerFooter() {
    var d = document.querySelector('cart-drawer');
    if (!d) return null;
    // the real emptiness, not the host class -- see syncEmpty
    if (syncEmpty(d)) return null;
    return d.querySelector('.drawer__footer');
  }

  function mount() {
    if (mounting) return;
    var extras = extrasNode();
    var footer = drawerFooter();
    if (!extras || !footer) return;
    if (extras.parentElement !== footer || extras.previousElementSibling) {
      mounting = true;
      footer.insertBefore(extras, footer.firstChild);
      mounting = false;
    }
  }

  /* --------------------------------------------------------------- render -- */

  /**
   * item.price === null renders no price cell at all. The quantity-ladder row
   * uses that: it is an invitation to add another of something already priced
   * in the cart above it. It used to render a price and then strip the span
   * again with a regex, which a nested element inside the span would have
   * silently broken, leaving a "$0" on the row.
   *
   * A reference price is always labelled AT LAUNCH, never left as a bare
   * strikethrough: nothing on this store has ever sold at one, so every
   * compare-at here is a price that has never been charged.
   * → decisions/2026-09-20-a-reference-price-never-charged-is-labelled-at-launch-not-rrp.md
   */
  function rowHTML(item) {
    // THE REFERENCE PRICE IS THE SUB-LINE, not a second line in the price cell.
    // Stacked under the price it made that column ~86px wide on a 329px drawer,
    // which left the body too narrow: "Heavy Duty Carry Case" and "SHIPS WITH
    // THE DEVICE" both wrapped, so each accessory row ran four lines. Four-line
    // rows pushed the footer tall enough to clip the line item's own quantity
    // stepper on a short phone. As the sub-line it costs no extra height at all,
    // reads larger, and the row is back to two lines.
    //
    // "SHIPS WITH THE DEVICE" gives up that slot when there is a reference price
    // to show. It is still on the product page's kit row, which is where the
    // decision is actually made; in the cart the price is the useful fact.
    var sub = item.sub;
    if (!sub) {
      sub = (item.compareAt && item.compareAt > item.price)
        ? '<s>' + money(item.compareAt) + '</s> ' + (T.atLaunch || 'AT LAUNCH')
        : T.shipsWith;
    }
    return '' +
      '<div class="cx-row" data-cx-variant="' + item.variantId + '">' +
        (item.image ? '<img src="' + item.image + '" alt="" loading="lazy" width="42" height="42">' : '') +
        '<div class="cx-row__body">' +
          '<div class="cx-row__name">' + item.title + '</div>' +
          '<div class="cx-row__sub">' + sub + '</div>' +
        '</div>' +
        (item.price != null ? '<span class="cx-row__price">' + money(item.price) + '</span>' : '') +
        '<button type="button" class="cx-btn" data-cx-add="' + item.variantId + '">' + T.add + '</button>' +
      '</div>';
  }

  function render(cart) {
    var extras = extrasNode();
    if (!extras) return;
    if (!cart || cart.item_count === 0) { extras.hidden = true; return; }
    LAST = cart;
    if (mergeSplitLines(cart)) return;   // re-renders once the merge lands

    var inCart = {};
    var heroQty = 0;
    cart.items.forEach(function (i) {
      inCart[i.variant_id] = (inCart[i.variant_id] || 0) + i.quantity;
      if (i.handle === CFG.hero.handle) heroQty += i.quantity;
    });

    // add-ons the cart does not already contain
    var rows = (CFG.addons || []).filter(function (a) { return !inCart[a.variantId]; });
    var up = el('cx-upsells');
    el('cx-upsell-rows').innerHTML = rows.map(rowHTML).join('');
    up.hidden = rows.length === 0;

    // quantity ladder, only when a real discount backs it and the device is in
    // the cart below the cap
    var tiers = (CFG.tiers || []).filter(function (t) { return t.pct > 0; });
    var tiersEl = el('cx-tiers');
    // a flat minimum-quantity discount is already earned at the first tier, so
    // once the buyer is at or past it there is nothing left to advertise.
    // Filter BEFORE deciding to show the row (2026-10-03): with two units in the
    // cart there is no tier left, `next` was undefined, `next.pct` threw, and the
    // throw skipped mount() below -- so the add-ons and the free-shipping line
    // vanished from the drawer the moment a second Codex was added.
    tiers = tiers.filter(function (t) { return t.qty > heroQty; });
    var showTiers = tiers.length > 0 && heroQty > 0 && heroQty < (CFG.hero.maxQty || 3);
    if (showTiers) {
      var next = tiers[0];
      // The discount applies to EVERY unit, so the whole saving is earned by the
      // decision to add one more. What that click actually costs is the new
      // discounted total minus the current one -- $174.30 on a $249 device, not
      // $249. The product page's kit row states it the same way; a drawer that
      // said "15% off at 2 or more" and left the buyer to work it out was the
      // same offer told worse.
      var pctAt = function (q) {
        var t = (CFG.tiers || []).filter(function (x) { return x.pct > 0 && x.qty <= q; }).pop();
        return t ? t.pct : 0;
      };
      var unit = CFG.hero.price;
      var marginal = null, saving = 0;
      if (unit) {
        var nowTotal  = heroQty * unit * (100 - pctAt(heroQty)) / 100;
        var nextTotal = (heroQty + 1) * unit * (100 - pctAt(heroQty + 1)) / 100;
        // whole dollars, rounded DOWN to match the product page's kit row
        marginal = Math.floor((nextTotal - nowTotal) / 100) * 100;
        saving = Math.round((heroQty + 1) * unit * pctAt(heroQty + 1) / 100);
      }
      var tierSub = marginal != null
        ? '<s>' + money(unit) + '</s> · ' + next.pct + '% off both'
        : next.pct + '% off every Codex at ' + next.qty + ' or more';
      el('cx-tier-rows').innerHTML = next ? rowHTML({
        variantId: CFG.hero.variantId,
        title: (T.extraTitle || 'Extra') + ' ' + CFG.hero.title,
        sub: tierSub,
        image: CFG.hero.image,
        price: marginal        // null falls back to no price cell -- see rowHTML
      }) : '';
    }
    tiersEl.hidden = !showTiers || !el('cx-tier-rows').innerHTML;

    // One heading over every offer; with nothing left to offer, no heading and no block.
    var anyOffer = !up.hidden || !tiersEl.hidden;
    el('cx-head').textContent = T.heading;
    el('cx-head').hidden = !anyOffer;

    renderSavings(cart);
    extras.hidden = !anyOffer;
    mount();
  }

  function renderSavings(cart) {
    var saved = 0;
    cart.items.forEach(function (i) {
      var was = i.original_price != null ? i.original_price : i.price;
      var compare = i.compare_at_price || was;
      var now = i.final_price != null ? i.final_price : i.price;
      if (compare > now) saved += (compare - now) * i.quantity;
    });
    var host = document.querySelector('cart-drawer .totals');
    var existing = document.getElementById('cx-save');
    if (!host || saved <= 0) { if (existing) existing.remove(); return; }
    if (!existing) {
      existing = document.createElement('div');
      existing.id = 'cx-save';
      existing.className = 'cx-save';
      host.parentElement.insertBefore(existing, host);
    }
    existing.innerHTML = '<span>' + T.save + '</span><span class="cx-save__value">' + money(saved) + '</span>';
  }

  /* ------------------------------------------------------------- add flow -- */

  function sectionsToRender() {
    var d = document.querySelector('cart-drawer');
    try { return d.getSectionsToRender().map(function (s) { return s.id; }); }
    catch (e) { return ['cart-drawer', 'cart-icon-bubble']; }
  }

  /**
   * 2026-10-28: ADD raises the quantity of a line already in the cart instead of adding a new one.
   * The product page adds with a "Pre-order" line property and this button used to add without it;
   * Shopify keeps lines with different properties apart, so a second Codex became a second line of
   * one. Now: a line for this variant exists -> /cart/change.js on its key, quantity + 1; otherwise
   * /cart/add.js with the same property the product page uses, so later adds merge with it.
   */
  function addRequest(variantId) {
    var line = null;
    (LAST && LAST.items || []).forEach(function (i) { if (!line && i.variant_id === variantId) line = i; });
    var sections = sectionsToRender();
    if (line) {
      return fetch((window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : '/cart/change') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: line.quantity + 1, sections: sections, sections_url: window.location.pathname })
      });
    }
    var P = CFG.preorder || {};
    var prop = variantId === CFG.hero.variantId ? P.hero : P.addon;
    var item = { id: variantId, quantity: 1 };
    if (prop) item.properties = { 'Pre-order': prop };
    return fetch(window.routes && window.routes.cart_add_url ? window.routes.cart_add_url + '.js' : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item], sections: sections })
    });
  }

  /**
   * Carts built before 2026-10-28 can hold the same variant on two lines (one with the property, one
   * without). Merge them into the first line once, quietly: /cart/update.js keyed by line key, then the
   * drawer's sections are repainted WITHOUT renderContents(), which would pop the drawer open on page
   * load. The 15% at 2+ discount counts units, not lines, so the price does not change.
   * Returns true when a merge was started.
   */
  var MERGED = {};
  function mergeSplitLines(cart) {
    var first = {}, updates = null, sig = [];
    cart.items.forEach(function (i) {
      if (!first[i.variant_id]) { first[i.variant_id] = i; return; }
      updates = updates || {};
      var keep = first[i.variant_id];
      updates[keep.key] = (updates[keep.key] || keep.quantity) + i.quantity;
      updates[i.key] = 0;
      sig.push(i.key);
    });
    if (!updates) return false;
    sig = sig.join(',');
    if (MERGED[sig]) return false;   // tried once already; never loop
    MERGED[sig] = true;
    var drawer = document.querySelector('cart-drawer');
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates, sections: sectionsToRender(), sections_url: window.location.pathname })
    })
      .then(function (r) { if (!r.ok) throw new Error('merge'); return r.json(); })
      .then(function (state) { if (drawer && state.sections) paint(drawer, state); })
      .catch(function () {})
      .then(function () { refresh(); });
    return true;
  }

  // CartDrawer.renderContents() without its open(): replace each section's markup in place.
  function paint(drawer, state) {
    var list;
    try { list = drawer.getSectionsToRender(); } catch (e) { return; }
    list.forEach(function (sec) {
      var html = state.sections[sec.id];
      var target = sec.selector ? document.querySelector(sec.selector) : document.getElementById(sec.id);
      if (!html || !target) return;
      var src = new DOMParser().parseFromString(html, 'text/html').querySelector(sec.selector || '.shopify-section');
      if (src) target.innerHTML = src.innerHTML;
    });
    syncEmpty(drawer);
  }

  function addVariant(variantId, btn) {
    var drawer = document.querySelector('cart-drawer');
    btn.disabled = true;
    btn.textContent = T.adding;
    addRequest(variantId)
      .then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); })
      .then(function (state) {
        btn.textContent = T.added;
        btn.classList.add('cx-btn--done');
        if (drawer && state.sections) drawer.renderContents(state);
        return refresh();
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = T.add;
        var row = btn.closest('.cx-row');
        if (row && !row.querySelector('.cx-err')) {
          var e = document.createElement('p');
          e.className = 'cx-err';
          e.textContent = T.error;
          row.appendChild(e);
          setTimeout(function () { if (e.parentNode) e.remove(); }, 4000);
        }
      });
  }

  document.addEventListener('click', function (ev) {
    var btn = ev.target.closest ? ev.target.closest('[data-cx-add]') : null;
    if (!btn) return;
    ev.preventDefault();
    addVariant(parseInt(btn.getAttribute('data-cx-add'), 10), btn);
  });

  /* ----------------------------------------------------------------- run -- */

  var pending;
  function refresh() {
    clearTimeout(pending);
    return new Promise(function (resolve) {
      pending = setTimeout(function () {
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) { render(cart); resolve(cart); })
          .catch(resolve);
      }, 120);
    });
  }
  window.codexCartRefresh = refresh;

  function watch() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    new MutationObserver(function () {
      syncEmpty(drawer);
      // extrasNode() re-adopts the block if the render just destroyed it, so a
      // null here really does mean the page is going away.
      var extras = extrasNode();
      if (!extras) return;
      if (!drawer.contains(extras)) refresh();
      else mount();
    }).observe(drawer, { childList: true, subtree: true });
    drawer.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('cart-remove-button')) refresh();
    });
  }

  /**
   * Patch the custom element itself as well as watching it. The observer below
   * catches every render, but it runs as a microtask AFTER the mutation, while
   * renderContents() schedules open() on a timeout; patching the prototype puts
   * the class right inside the same call, before anything can read it. Both are
   * kept: the observer also covers CartDrawerItems, which re-renders
   * .drawer__inner without going through CartDrawer.renderContents at all.
   */
  function patchDrawer() {
    var Ctor = window.customElements && customElements.get('cart-drawer');
    if (!Ctor || Ctor.prototype.__codexEmptySync) return;
    var orig = Ctor.prototype.renderContents;
    if (typeof orig !== 'function') return;
    Ctor.prototype.renderContents = function (state) {
      var out = orig.apply(this, arguments);
      syncEmpty(this);
      return out;
    };
    Ctor.prototype.__codexEmptySync = true;
  }

  function boot() { patchDrawer(); syncEmpty(); watch(); refresh(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
